type Utif = Pick<typeof import('utif2'), 'decode' | 'decodeImage' | 'toRGBA8'>;

/** About a 600 dpi A4 scan. Decoding runs on the main thread, so a larger declared page is refused unread. */
export const TIFF_MAX_PIXELS = 50_000_000;
const TIFF_IMAGE_WIDTH_TAG = 't256';
const TIFF_IMAGE_LENGTH_TAG = 't257';

/** Converts a TIFF scan or fax (its first page) to PNG. utif2 is large, so it loads only when a TIFF is opened. */
export async function tiffToPng(blob: Blob): Promise<Blob> {
  const utif = await loadUtif();
  const buffer = await blob.arrayBuffer();
  const [page] = utif.decode(buffer);
  if (!page) {
    throw new Error('The TIFF holds no image');
  }
  const width = firstNumber(page[TIFF_IMAGE_WIDTH_TAG]);
  const height = firstNumber(page[TIFF_IMAGE_LENGTH_TAG]);
  if (width === undefined || height === undefined || width * height > TIFF_MAX_PIXELS) {
    throw new Error('The TIFF page is too large to preview');
  }
  utif.decodeImage(buffer, page);
  const canvas = document.createElement('canvas');
  canvas.width = page.width;
  canvas.height = page.height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('The browser has no canvas to draw the TIFF on');
  }
  const pixels = context.createImageData(page.width, page.height);
  pixels.data.set(utif.toRGBA8(page));
  context.putImageData(pixels, 0, 0);
  return new Promise((resolve, reject) =>
    canvas.toBlob((png) => (png ? resolve(png) : reject(new Error('The TIFF could not be drawn'))), 'image/png'),
  );
}

/**
 * utif2 is CommonJS. Its types name its functions as exports, but the bundler's lazy chunk and Node both hand the
 * module object over as `default` (SAI-119 review B-01), so the functions are looked up there first.
 */
async function loadUtif(): Promise<Utif> {
  const loaded: object = await import('utif2');
  const candidate: unknown = 'default' in loaded ? loaded.default : loaded;
  if (!isUtif(candidate)) {
    throw new Error('The TIFF decoder did not load');
  }
  return candidate;
}

function firstNumber(value: unknown): number | undefined {
  return Array.isArray(value) && typeof value[0] === 'number' ? value[0] : undefined;
}

function isUtif(value: unknown): value is Utif {
  return (
    typeof value === 'object' &&
    value !== null &&
    'decode' in value &&
    typeof value.decode === 'function' &&
    'decodeImage' in value &&
    typeof value.decodeImage === 'function' &&
    'toRGBA8' in value &&
    typeof value.toRGBA8 === 'function'
  );
}
